/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Form {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section, Loader, Button } from "@bw/bits"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { Formik, Form as FormWrap } from "formik"
import styled from "styled-components"
import { toast } from "react-toastify"
import {
  displayField,
  postFormFields,
  getFormFields,
  getInitialValues,
  fieldsSchema,
} from "./form-actions"

const StyledError = styled.div`
  color: red;
  margin: 2px 0 10px;
  font-size: 12px;
  line-height: 14px;
`

const Form = ({ suptitle, title, subtitle, form }) => {
  const [loading, setLoading] = React.useState(true)
  const [fields, setFields] = React.useState([])
  const { t } = useTranslation()

  const { data, isError, status } = useQuery(
    `form_${form.handle}`,
    async () => {
      return getFormFields(form.handle)
    }
  )

  React.useEffect(() => {
    if (isError) {
      toast.error(t("form.loadingError"))
    }

    if (!isError && status === "success") {
      setFields(data)
    }

    setLoading(false)
  }, [isError, t, data, status])

  const schema = fieldsSchema(fields)

  const formSchema = yup.object().shape({
    ...schema,
  })

  return (
    <Section suptitle={suptitle} title={title} subtitle={subtitle}>
      {loading && (
        <Loader
          messages={["Loading the page", "It takes longer than expected..."]}
          treshold={2}
        />
      )}

      {!loading && (
        <Formik
          initialValues={getInitialValues(fields)}
          validationSchema={formSchema}
          onSubmit={values =>
            postFormFields(form.handle, values)
              .then(() => {
                toast.success(t("form.sentSuccess"))
              })
              .catch(err => {
                toast.error(t("form.sentFail"))

                // these are server validation errors
                err?.response?.data?.errors &&
                  Object.values(err.response.data.errors).forEach(error => {
                    toast.error(error.join("\n"))
                  })
              })
          }
        >
          {({ errors, touched }) => (
            <>
              <FormWrap>
                {Object.values(fields).map(field => (
                  <React.Fragment key={field.handle}>
                    {displayField(field)}
                    {errors && touched && (
                      <StyledError>{errors[field.handle]}</StyledError>
                    )}
                  </React.Fragment>
                ))}
                <p>
                  <Button submit label={t("form.buttonSubmit")} />
                </p>
              </FormWrap>
            </>
          )}
        </Formik>
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on FormModule {
 *   ...FormModuleFragment
 * }
 *
 * This is also what you will get in the props of your Form
 * resolver component.
 */
export const query = graphql`
  fragment FormModuleFragment on FormModule {
    form {
      api_url
      handle
    }
  }
`

export default Form
